import * as React from 'react'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { IComboBox } from '@fluentui/react'
import { getDropdownProps } from '../../../../helpers/formHelper'
import LegacyFilePartyDropdown, { IFilePartyDropdownOption } from '../../../Dropdown/LegacyFilePartyDropdown'
import { getFilePartyFromMeta, InvoiceProps } from './InvoiceForm'
import { useFormikContext } from 'formik'
import { FilePartyMetaDataDto, InvoiceMutationDto, InvoiceType } from '../../../../config/finance/invoiceConfig'
import { FilePartyRole } from '../../../../config/filePartyConfig'
import { Link } from 'react-router-dom'
import { useSettings } from '../../../../hooks/useSettings'
import { InvoiceRecipientDto } from '../../../../context/GlobalContext'
import { useTranslation } from 'react-i18next'
import { Button, Field, makeStyles, MessageBar, MessageBarActions, MessageBarBody, shorthands, tokens } from '@fluentui/react-components'

const useStyles = makeStyles({
    formRow: {
        display: 'grid',
        gridTemplateColumns: '50% 50%',
        ...shorthands.gap(tokens.spacingVerticalL),
        marginRight: tokens.spacingVerticalL,
        // marginBottom: tokens.spacingVerticalL,
    },
})

type Props = InvoiceProps & {}

const getFilePartyId = (role: FilePartyRole, filePartyMetaData: FilePartyMetaDataDto[], setting?: InvoiceRecipientDto): string => {
    const fileParties = new Map<string, string[]>([])
    filePartyMetaData.forEach(fileParty => {
        if (!fileParties.has(fileParty.role)) {
            fileParties.set(fileParty.role, [])
        }
        fileParties.get(fileParty.role).push(fileParty.pivotId)
    })

    //Find party based on the users setting
    let foundParty = undefined
    const oppositeRole = role === FilePartyRole.Debtor ? FilePartyRole.Client : FilePartyRole.Debtor
    if (role === FilePartyRole.Client && setting?.clients) {
        foundParty = setting?.clients?.map(clientRole => fileParties.get(clientRole)).filter(Boolean)?.[0]?.[0]
    } else if (role === FilePartyRole.Debtor && setting?.debtors) {
        foundParty = setting?.debtors?.map(clientRole => fileParties.get(clientRole)).filter(Boolean)?.[0]?.[0]
    }

    //Find default party
    if (!foundParty && fileParties.get(role)?.length === 1) {
        foundParty = fileParties.get(role)[0]
    } else if (!foundParty && fileParties.get(oppositeRole)?.length === 1) {
        foundParty = fileParties.get(oppositeRole)[0]
    }

    return foundParty
}

export default function InvoiceParties({ invoice, meta, useInitialValues, useInitialInvoice, isEditable }: Props): ReactElement {
    const { settings } = useSettings()
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const { errors, values, initialValues, setFieldValue } = useFormikContext<InvoiceMutationDto>()
    const [filePartyOptions, setFilePartyOptions] = useState<IFilePartyDropdownOption<FilePartyMetaDataDto>[]>()
    const filePartyRef = useRef<IComboBox>()
    const isEndUserInvoice = useMemo<boolean>(() => ![InvoiceType.Split].includes(values.type), [values.type])

    const fileParties = useMemo(() => meta.data.files?.flatMap(file => file.fileParties) || [], [meta.data.files])

    useEffect(() => {
        if (isEndUserInvoice) {
            setFilePartyOptions(
                fileParties?.map((fileParty: FilePartyMetaDataDto) => ({
                    key: fileParty.pivotId,
                    text: fileParty.displayName.replaceAll('\n', ' '),
                    role: fileParty.role,
                    data: fileParty,
                })),
            )
            setFieldValue(
                'clientPivotId',
                (useInitialInvoice && invoice.recipient?.clientId) || getFilePartyId(FilePartyRole.Client, fileParties, settings?.invoiceRecipient),
            )
            setFieldValue(
                'debtorPivotId',
                (useInitialInvoice && invoice.recipient?.debtorId) || getFilePartyId(FilePartyRole.Debtor, fileParties, settings?.invoiceRecipient),
            )
        } else {
            setFieldValue('clientPivotId', undefined)
            setFieldValue('debtorPivotId', undefined)
        }
    }, [meta.data, isEndUserInvoice])

    useEffect(() => {
        if (isEndUserInvoice) {
            if (values.debtorPivotId) {
                const recipient = getFilePartyFromMeta(values.debtorPivotId, undefined, undefined, fileParties)?.[0]
                const recipients = getFilePartyFromMeta(undefined, recipient?.role, recipient?.id, fileParties)
                const referenceRequired = recipients
                    .map(recipient => recipient.referenceRequired)
                    .filter(Boolean)
                    .reduce((a, b) => a || b, false)

                setFieldValue('recipientReferenceRequired', referenceRequired)

                if (useInitialInvoice && initialValues.debtorPivotId === values.debtorPivotId) {
                    setFieldValue('recipientReference', invoice.recipient?.reference)
                    setFieldValue('recipientEmail', invoice.recipient?.email)
                } else if (useInitialValues) {
                    setFieldValue('recipientReference', initialValues.recipientReference)
                    setFieldValue('recipientEmail', initialValues.recipientEmail)
                } else {
                    setFieldValue(
                        'recipientReference',
                        recipients
                            .map(recipient => recipient.reference)
                            .filter(Boolean)
                            .filter((reference, index) => recipients.map(recipient => recipient.reference).indexOf(reference) === index)
                            .join(', '),
                    )
                    setFieldValue(
                        'recipientEmail',
                        meta.data.files
                            .map(file => file.file.invoiceEmail)
                            .filter(Boolean)
                            .filter((email, index) => meta.data.files.map(file => file.file.invoiceEmail).indexOf(email) === index)
                            .join(', ')
                            .trim() ||
                            recipients
                                .map(recipient => recipient.email)
                                .filter(Boolean)
                                .filter((email, index) => recipients.map(recipient => recipient.email).indexOf(email) === index)
                                .join(', '),
                    )
                }
            }
        } else {
            setFieldValue('recipientReference', undefined)
            setFieldValue('recipientEmail', undefined)
            setFieldValue('recipientReferenceRequired', false)
        }
    }, [meta.data.files, values.debtorPivotId, isEndUserInvoice])

    if (!meta.loading && fileParties.length === 0) {
        return (
            <MessageBar intent={'error'}>
                <MessageBarBody>
                    Dit dossier bevat geen debiteuren of cli&euml;nten met geldige adresgegevens. Ga naar het dossier om dit te verhelpen.
                </MessageBarBody>
                <MessageBarActions>
                    <Button>
                        <Link to={`/file/${meta.data.files[0].file.id}/party`}>Dossier</Link>
                    </Button>
                </MessageBarActions>
            </MessageBar>
        )
    }

    return (
        <div className={styles.formRow}>
            <Field label={translate('invoice.clientpivotid')} orientation={'vertical'} required>
                <LegacyFilePartyDropdown
                    required
                    disabled={meta.loading || !isEditable}
                    options={filePartyOptions}
                    componentRef={filePartyRef}
                    {...getDropdownProps(values.clientPivotId, errors?.clientPivotId, value => setFieldValue('clientPivotId', value))}
                />
            </Field>
            <Field label={translate('invoice.debtorpivotid')} orientation={'vertical'} required>
                <LegacyFilePartyDropdown
                    required
                    disabled={meta.loading || !isEditable}
                    options={filePartyOptions}
                    {...getDropdownProps(values.debtorPivotId, errors?.debtorPivotId, value => setFieldValue('debtorPivotId', value))}
                />
            </Field>
        </div>
    )
}
